import React, { useState, useEffect } from "react";
import { getCurrentUser, logout } from "./api";
import Login from "./components/Login";
import ResetPasswordScreen from "./components/ResetPasswordScreen";
import ShellV3 from "./components/ShellV3";
import ShellV4 from "./components/ShellV4";
import { ToastProvider } from "./components/Toast";
import { ThemeProvider } from "./components/Theme";
import { VersionProvider, useVersion } from "./components/Version";
import BrowserSupportBanner from "./components/BrowserSupportBanner";

// Read the `?reset=<token>` query param on first paint. If present, we
// route the whole app to the reset-password screen — supersedes both
// the login and shell paths until the user completes or cancels reset.
function readResetToken() {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("reset");
    return t && t.length > 8 ? t : null;
  } catch { return null; }
}

// One unified shell now — the old League and Umpire workspaces have
// been merged into Gabruz Universe. League views live under the
// League sidebar section (committee-role-gated); Officiating (umpire
// polls) is reached via a Home dashboard tile for UMPIRE users. See
// nav.js DESKTOP_SECTIONS and Module.jsx routes.
function AppInner({ user, setUser }) {
  const { version } = useVersion();
  const [resetToken, setResetToken] = useState(readResetToken);

  // If we're mid-reset, force sign-out — a logged-in user shouldn't
  // land on the reset page carrying their previous session.
  useEffect(() => {
    if (resetToken && user) {
      logout();
      setUser(null);
    }
  }, [resetToken, user, setUser]);

  if (resetToken) {
    return (
      <ResetPasswordScreen
        token={resetToken}
        onDone={() => {
          // Strip ?reset= from the URL, drop back to the login screen.
          try {
            const url = new URL(window.location.href);
            url.searchParams.delete("reset");
            window.history.replaceState({}, "", url.pathname + (url.search ? url.search : ""));
          } catch { /* ignore */ }
          setResetToken(null);
        }}
      />
    );
  }

  if (!user) return <Login onLogin={setUser} />;
  if (version === "v4") return <ShellV4 user={user} setUser={setUser} />;
  return <ShellV3 user={user} setUser={setUser} />;
}

export default function App() {
  const [user, setUser] = useState(getCurrentUser());
  return (
    <VersionProvider>
      <ThemeProvider>
        <ToastProvider>
          <BrowserSupportBanner />
          <AppInner user={user} setUser={setUser} />
        </ToastProvider>
      </ThemeProvider>
    </VersionProvider>
  );
}
